<template>
  <div class="ordem-estrutura">
    <div v-if="isRoot && (!items || items.length === 0)" class="text-muted text-center py-4">
      Nenhum nível cadastrado neste plano.
    </div>

    <div v-if="isRoot && items && items.length > 0" class="ordem-estrutura-hint mb-3">
      <b-alert show variant="light" class="py-2 px-3 mb-0 d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center">
          <img src="/icons/drag-02.png" class="mr-2" alt="drag" style="width:20px;height:20px;" />
          Arraste e solte os itens para reordenar os níveis dentro de cada agrupamento.
        </div>
        <div class="d-flex align-items-center ml-3" style="flex-shrink: 0;">
          <b-button
            size="sm"
            variant="outline-secondary"
            class="mr-1"
            @click="expandAll"
            title="Expandir todos"
          >
            <b-icon icon="arrows-expand" /> Expandir
          </b-button>
          <b-button
            size="sm"
            variant="outline-secondary"
            @click="collapseAll"
            title="Recolher todos"
          >
            <b-icon icon="arrows-collapse" /> Recolher
          </b-button>
        </div>
      </b-alert>
    </div>

    <draggable
      v-model="localItems"
      handle=".drag-handle"
      group="estrutura"
      animation="150"
      :force-fallback="true"
      :scroll-sensitivity="80"
      :scroll-speed="12"
      fallback-class="ordem-estrutura-fallback"
      @end="onDragEnd"
      class="draggable-list"
    >
      <div
        v-for="item in localItems"
        :key="item.id"
        class="ordem-estrutura-node"
      >
        <div class="ordem-estrutura-row">
          <span class="drag-handle" title="Arrastar para reordenar">
            <b-icon icon="grip-vertical" />
          </span>
          <div class="ordem-estrutura-info">
            <span class="ordem-estrutura-level-label text-muted">{{ levelName }}</span>
            <span class="ordem-estrutura-name">{{ item.name }}</span>
          </div>
          <button
            v-if="item.children && item.children.length > 0"
            class="ordem-estrutura-toggle"
            :title="isExpanded(item.id) ? 'Recolher' : 'Expandir'"
            @click="toggleNode(item.id)"
          >
            <b-icon :icon="isExpanded(item.id) ? 'chevron-up' : 'chevron-down'" />
            <span class="ordem-estrutura-toggle-count">{{ item.children.length }}</span>
          </button>
        </div>

        <b-collapse :visible="isExpanded(item.id)" v-if="item.children && item.children.length > 0">
          <div class="ordem-estrutura-children">
            <OrdemEstruturaChild
              :structure="item.children"
              :deep="childDeep"
              :strategic-plan-id="strategicPlanId"
              :collapsed-set="collapsedNodes"
              @reordered="onChildReordered"
              @update:collapsed-set="onChildCollapsedUpdate"
            />
          </div>
        </b-collapse>
      </div>
    </draggable>
  </div>
</template>

<script>
import draggable from "vuedraggable";
import { mapState, mapActions } from "vuex";

export default {
  name: "OrdemEstrutura",

  components: {
    draggable,
    OrdemEstruturaChild: () => import("./OrdemEstrutura"),
  },

  props: {
    structure: {
      type: Array,
      default: null,
    },
    deep: {
      type: Number,
      default: 0,
    },
    strategicPlanId: {
      type: Number,
      default: null,
    },
    collapsedSet: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      localItems: [],
      saving: false,
      collapsedNodes: {},
    };
  },

  computed: {
    ...mapState("strategic_plans", ["strategic_plan", "tree_plan_structure"]),

    isRoot() {
      return this.deep === 0;
    },

    items() {
      return this.isRoot ? this.tree_plan_structure : this.structure;
    },

    levels() {
      return (
        this.strategic_plan &&
        this.strategic_plan.config &&
        this.strategic_plan.config.plan_structure &&
        this.strategic_plan.config.plan_structure.levels
      ) || [];
    },

    levelName() {
      return this.levels[this.deep] || `Nível ${this.deep + 1}`;
    },

    childDeep() {
      return this.deep + 1;
    },

    currentStrategicPlanId() {
      return this.strategicPlanId || (this.strategic_plan && this.strategic_plan.id);
    },
  },

  watch: {
    items: {
      immediate: true,
      deep: true,
      handler(val) {
        this.localItems = val ? [...val] : [];
      },
    },
    collapsedSet: {
      immediate: true,
      handler(val) {
        if (val && !this.isRoot) {
          this.collapsedNodes = val;
        }
      },
    },
  },

  methods: {
    ...mapActions("strategic_plans", [
      "ActionOrderPlanStructuresById",
      "ActionLoadPlanStructure",
    ]),

    isExpanded(nodeId) {
      return !this.collapsedNodes[nodeId];
    },

    toggleNode(nodeId) {
      this.$set(this.collapsedNodes, nodeId, !this.collapsedNodes[nodeId]);
      if (this.isRoot) {
        this.$forceUpdate();
      } else {
        this.$emit("update:collapsed-set", this.collapsedNodes);
      }
    },

    expandAll() {
      this.collapsedNodes = {};
      this.$forceUpdate();
    },

    collapseAll() {
      const collapsed = {};
      this.collectAllNodeIds(this.localItems, collapsed);
      this.collapsedNodes = collapsed;
      this.$forceUpdate();
    },

    collectAllNodeIds(items, target) {
      if (!items) return;
      for (const item of items) {
        if (item.children && item.children.length > 0) {
          target[item.id] = true;
          this.collectAllNodeIds(item.children, target);
        }
      }
    },

    onChildCollapsedUpdate(val) {
      this.collapsedNodes = val;
      if (!this.isRoot) {
        this.$emit("update:collapsed-set", this.collapsedNodes);
      }
    },

    async onDragEnd() {
      const orderedItems = this.localItems.map((item, index) => ({
        id: item.id,
        order: index + 1,
      }));

      await this.saveOrder(orderedItems);
    },

    async onChildReordered(orderedItems) {
      await this.saveOrder(orderedItems);
    },

    async saveOrder(orderedItems) {
      if (this.saving) return;
      this.saving = true;
      try {
        await this.ActionOrderPlanStructuresById({
          id: this.currentStrategicPlanId,
          items: orderedItems,
        });
        this.$notifications.addNotification({
          message: "Ordem alterada com sucesso",
          type: "success",
        });
        await this.ActionLoadPlanStructure();
      } catch {
        this.$notifications.addNotification({
          message: "Erro ao salvar a ordem",
          icon: "exclamation-triangle",
          type: "danger",
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.ordem-estrutura-node {
  margin-bottom: 4px;
}

.ordem-estrutura-row {
  display: flex;
  align-items: center;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 8px 12px;
  gap: 10px;
  cursor: default;
}

.drag-handle {
  cursor: grab;
  color: #aaa;
  font-size: 1.1rem;
  flex-shrink: 0;
  padding: 2px 4px;
  touch-action: none;
}

.drag-handle:active {
  cursor: grabbing;
}

.ordem-estrutura-info {
  display: flex;
  flex-direction: column;
  gap: 1px;
  flex: 1;
  min-width: 0;
}

.ordem-estrutura-level-label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.ordem-estrutura-name {
  font-weight: 500;
  font-size: 0.95rem;
  color: #333;
}

.ordem-estrutura-toggle {
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 3px 8px;
  color: #6c757d;
  cursor: pointer;
  font-size: 0.82rem;
  flex-shrink: 0;
  transition: background 0.15s, color 0.15s;
}

.ordem-estrutura-toggle:hover {
  background: #f0f0f0;
  color: #495057;
}

.ordem-estrutura-toggle-count {
  font-size: 0.75rem;
  font-weight: 600;
  color: #868e96;
}

.ordem-estrutura-children {
  margin-left: 28px;
  margin-top: 4px;
  border-left: 2px solid #e9ecef;
  padding-left: 10px;
}

.draggable-list {
  min-height: 10px;
}

.sortable-ghost .ordem-estrutura-row {
  opacity: 0.4;
  background: #e8f0fe;
  border-color: #335080;
}

.sortable-chosen .ordem-estrutura-row {
  box-shadow: 0 2px 8px rgba(51, 80, 128, 0.25);
}

.ordem-estrutura-fallback {
  opacity: 0.9;
  box-shadow: 0 4px 16px rgba(51, 80, 128, 0.3);
}
</style>
